// @flow
import React, { useMemo, useEffect, useState, type Node } from 'react';
import isEmpty from 'ramda/es/isEmpty';

import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon';

import { useVerificationsDataContext } from '../VerificationsData';


/**
 *  Иконка отображется в столбце с оценками и показывает степень готовности задания
 * (степень проверки учителем вложенных учеником файлов)
 *
 *  По умолчанию UploadFileIcon ни один файл не проверен или ученик еще не прилаживал файлы
 *  UploadedIcon -- несколько файлов проверено, но не все
 *  CheckedIcon -- все приложенные учеником файлы проверены
 */

type Props = {
  className?: string,
  solutionId: number,
  attachedFilesIds: Array<number>,
}

const SolutionIcon = ({ className, solutionId, attachedFilesIds }: Props): Node => {
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationsBySolutionId, actions] = useVerificationsDataContext();
  const verifications = verificationsBySolutionId[String(solutionId)];

  useEffect(() => {
    (async () => {
      setVerificationLoading(true);
      await actions.requestVerifications(solutionId);
      setVerificationLoading(false);
    })();
    return () => {
      actions.deleteVerificationBySolutionId(solutionId);
    };
  }, [solutionId]);

  const verificationRate: 'not_yet_verified' | 'all_verified' | 'partial_verified' = useMemo(() => {
    if (verificationLoading || isEmpty(verifications) || attachedFilesIds.length === 0) {
      return 'not_yet_verified';
    }
    if (attachedFilesIds.length === verifications.length) {
      return 'all_verified';
    }
    return 'partial_verified';
  }, [verifications, attachedFilesIds, verificationLoading]);

  if (verificationLoading) {
    return null;
  }

  if (verificationRate === 'not_yet_verified') {
    return (
      <PlainIcon
        type={iconTypes.UPLOAD_FILE}
        className={className}
        width={20}
        height="auto"
      />
    );
  }

  if (verificationRate === 'all_verified') {
    return (
      <PlainIcon
        type={iconTypes.CHECKED_DOUBLE}
        className={className}
        width={20}
        height="auto"
      />

    );
  }

  return (
    <PlainIcon
      type={iconTypes.CHECKED}
      className={className}
      width={20}
      height="auto"
    />
  );
};

export default SolutionIcon;
